import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { X, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPES = ["stay", "wedding", "retreat"];

const initialForm = { name: "", email: "", phone: "", date: "", party_size: 2, message: "" };

export default function EnquiryModal({ t, open, type, onClose, onTypeChange }) {
  const [form, setForm] = useState(initialForm);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (open) {
      setSent(false);
      setForm(initialForm);
    }
  }, [open]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, {
        ...form,
        party_size: Number(form.party_size),
        type,
        date: form.date || null,
        phone: form.phone || null,
      });
      setSent(true);
      toast.success(t.toast.success);
    } catch (err) {
      toast.error(t.toast.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="enquiry-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-6 bg-pine/60 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 60, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="bg-ivory w-full max-w-xl max-h-[92vh] overflow-y-auto p-8 md:p-10 relative"
            onClick={(e) => e.stopPropagation()}
            data-lenis-prevent
          >
            <button
              data-testid="enquiry-close-btn"
              onClick={onClose}
              className="absolute top-5 right-5 text-pine/50 hover:text-terracotta transition-colors"
              aria-label="Close enquiry form"
            >
              <X size={22} />
            </button>

            {sent ? (
              <div className="py-10 text-center" data-testid="enquiry-success">
                <span className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-pine text-ivory mb-6">
                  <Check size={24} />
                </span>
                <h3 className="font-serif font-light text-3xl text-pine mb-3">{t.modal.successTitle}</h3>
                <p className="text-pine/60 text-base leading-relaxed max-w-sm mx-auto">
                  {t.modal.successBody}
                </p>
                <button
                  data-testid="enquiry-done-btn"
                  onClick={onClose}
                  className="mt-8 bg-pine text-ivory font-mono text-xs uppercase tracking-[0.2em] px-7 py-4 hover:bg-terracotta transition-colors"
                >
                  {t.modal.successBack}
                </button>
              </div>
            ) : (
              <>
                <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-bark mb-2">{t.modal.overline}</p>
                <h3 className="font-serif font-light text-3xl md:text-4xl text-pine mb-8">{t.modal.title}</h3>

                <div className="flex gap-2 mb-8" data-testid="enquiry-type-tabs">
                  {TYPES.map((key) => (
                    <button
                      key={key}
                      data-testid={`enquiry-type-${key}`}
                      onClick={() => onTypeChange(key)}
                      className={`flex-1 font-mono text-[11px] uppercase tracking-[0.15em] px-3 py-3 border transition-colors duration-300 ${
                        type === key
                          ? "bg-pine text-ivory border-pine"
                          : "border-pine/20 text-pine/60 hover:border-terracotta hover:text-terracotta"
                      }`}
                    >
                      {t.modal.types[key]}
                    </button>
                  ))}
                </div>

                <form data-testid="enquiry-form" onSubmit={submit} className="space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-pine/60 mb-2">{t.modal.fullName}</label>
                      <input
                        data-testid="enquiry-name-input"
                        required
                        value={form.name}
                        onChange={set("name")}
                        className="w-full bg-white border border-pine/15 px-4 py-3 text-pine text-sm focus:outline-none focus:border-terracotta transition-colors"
                        placeholder={t.modal.phName}
                      />
                    </div>
                    <div>
                      <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-pine/60 mb-2">{t.modal.email}</label>
                      <input
                        data-testid="enquiry-email-input"
                        required
                        type="email"
                        value={form.email}
                        onChange={set("email")}
                        className="w-full bg-white border border-pine/15 px-4 py-3 text-pine text-sm focus:outline-none focus:border-terracotta transition-colors"
                        placeholder={t.modal.phEmail}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                    <div>
                      <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-pine/60 mb-2">{t.modal.phone}</label>
                      <input
                        data-testid="enquiry-phone-input"
                        value={form.phone}
                        onChange={set("phone")}
                        className="w-full bg-white border border-pine/15 px-4 py-3 text-pine text-sm focus:outline-none focus:border-terracotta transition-colors"
                        placeholder={t.modal.phPhone}
                      />
                    </div>
                    <div>
                      <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-pine/60 mb-2">{t.modal.date}</label>
                      <input
                        data-testid="enquiry-date-input"
                        type="date"
                        value={form.date}
                        onChange={set("date")}
                        className="w-full bg-white border border-pine/15 px-4 py-3 text-pine text-sm focus:outline-none focus:border-terracotta transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-pine/60 mb-2">{t.modal.partySize}</label>
                      <input
                        data-testid="enquiry-party-size-input"
                        required
                        type="number"
                        min="1"
                        max="23"
                        value={form.party_size}
                        onChange={set("party_size")}
                        className="w-full bg-white border border-pine/15 px-4 py-3 text-pine text-sm focus:outline-none focus:border-terracotta transition-colors"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-pine/60 mb-2">{t.modal.message}</label>
                    <textarea
                      data-testid="enquiry-message-input"
                      required
                      rows={4}
                      value={form.message}
                      onChange={set("message")}
                      className="w-full bg-white border border-pine/15 px-4 py-3 text-pine text-sm focus:outline-none focus:border-terracotta transition-colors resize-none"
                      placeholder={t.modal.phMessage}
                    />
                  </div>
                  <button
                    data-testid="enquiry-submit-btn"
                    type="submit"
                    disabled={sending}
                    className="w-full bg-terracotta text-ivory font-mono text-xs uppercase tracking-[0.2em] px-7 py-4 hover:bg-clay transition-colors duration-300 disabled:opacity-50"
                  >
                    {sending ? t.modal.sending : t.modal.submit}
                  </button>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-pine/40 text-center">
                    {t.modal.footnote}
                  </p>
                </form>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
